<template>
    <v-container fill-height>
        <v-layout>
            <v-flex xs12 align-self-center ref="container">
                <transition type="animation" name="slide-y-transition">
                    <v-img src="/img/logo.png" justify-center />
                </transition>
                <div class="mx-4" v-if="loading">
                    <v-progress-linear indeterminate color="#00aa87"></v-progress-linear>
                </div>
            </v-flex>
        </v-layout>
    </v-container>
</template>

<script lang="ts">
    import {
        defineComponent,
        onBeforeMount,
        onBeforeUnmount,
        onMounted,
        ref,
        getCurrentInstance,
        computed,
        watch
    } from "vue";
    import { alarm, app, sensor } from "@/store";
    import { mapJsonToAlarmStatus } from "@/models/AlarmStatus";
    import { configureOidc } from "@/store/services/security.service";
    import { stopMessageHub } from "@/plugins/messageHub";
    import { OidcAuth } from "vue-oidc-client/vue2";
    import useIOSDetection from "@/utilities/useIOSDetection";
    import { configureMessageHub } from "../plugins/messageHub";

    export default defineComponent({
        setup() {
            const root = getCurrentInstance()?.proxy as any;
            sensor.GetData();

            const loading = ref<Boolean>(true);
            const container = ref<HTMLElement>();
            const error = computed(() => app.error);
            const statusReceived = ref(false);
            let statusCheckIntervalId: ReturnType<typeof setInterval> | null = null;
            let statusCheckTimeoutId: ReturnType<typeof setTimeout> | null = null;
            let activeStatusResolve: ((received: boolean) => void) | null = null;
            let logoutTimerId: ReturnType<typeof setTimeout> | null = null;
            let clearErrorTimerId: ReturnType<typeof setTimeout> | null = null;
            let stopStatusWatch: (() => void) | null = null;

            const delay = (ms: number) => new Promise(res => setTimeout(res, ms));
            const isDev = process.env.NODE_ENV === "development";

            const clearStatusWaitTimers = () => {
                if (statusCheckIntervalId) {
                    clearInterval(statusCheckIntervalId);
                    statusCheckIntervalId = null;
                }

                if (statusCheckTimeoutId) {
                    clearTimeout(statusCheckTimeoutId);
                    statusCheckTimeoutId = null;
                }
            };

            const settleStatusWait = (received: boolean) => {
                clearStatusWaitTimers();
                const resolve = activeStatusResolve;
                activeStatusResolve = null;
                if (resolve) {
                    resolve(received);
                }
            };

            const clearPageTimers = () => {
                clearStatusWaitTimers();

                if (stopStatusWatch) {
                    stopStatusWatch();
                    stopStatusWatch = null;
                }

                if (logoutTimerId) {
                    clearTimeout(logoutTimerId);
                    logoutTimerId = null;
                }

                if (clearErrorTimerId) {
                    clearTimeout(clearErrorTimerId);
                    clearErrorTimerId = null;
                }

                if (activeStatusResolve) {
                    const resolve = activeStatusResolve;
                    activeStatusResolve = null;
                    resolve(false);
                }
            };

            const getStatusFetchAt = () => {
                const fetchAt = alarm.alarmStatus?.fetchAt;
                return fetchAt instanceof Date ? fetchAt.getTime() : 0;
            };

            const waitForStatus = async (startedAtMs: number, timeoutMs = 15000): Promise<boolean> => {
                return await new Promise((resolve) => {
                    clearStatusWaitTimers();
                    activeStatusResolve = resolve;

                    if (getStatusFetchAt() > startedAtMs) {
                        settleStatusWait(true);
                        return;
                    }

                    statusCheckIntervalId = setInterval(() => {
                        if (statusReceived.value || getStatusFetchAt() > startedAtMs) {
                            settleStatusWait(true);
                        }
                    }, 300);

                    statusCheckTimeoutId = setTimeout(() => {
                        settleStatusWait(false);
                    }, timeoutMs);
                });
            };

            stopStatusWatch = watch(() => alarm.alarmStatus?.fetchAt, (val) => {
                if (val) {
                    statusReceived.value = true;
                    if (activeStatusResolve) {
                        settleStatusWait(true);
                    }
                }
            });

            const logout = () => {
                configureOidc().then((oidc: OidcAuth) => {
                    stopMessageHub().finally(() => oidc.signOut()).catch(err => {
                        console.error('OIDC configuration or sign-out failed:', err);
                        window.location.href = "/Identity/Account/Logout";
                    });
                });
            };

            onBeforeMount(() => {
                app.showFooter = false;
            });
            onBeforeUnmount(() => {
                app.showFooter = true;
                clearPageTimers();
            });

            onMounted(async () => {
                const maxRetries = 3;
                let attempt = 0;
                let success = false;

                try {
                    await configureMessageHub();
                    console.log("Message hub configured");

                    if (isDev) {
                        const sampleStatus =
                            '{"Al":"0","Tm":[{"A":"8D81","Tc":"2516"},{"A":"AC0F","Tc":"2534"}],"Pm":[{"A":"13F4","Pd":"0000000000000004"}],"Bm":[{"A":"8D81","Bp":"21"},{"A":"AC0F","Bp":"21"},{"A":"F104","Bp":"42"}],"Vs":[{"V1":"24.1","V2":"N/A","V3":"4.04","V4":"0.26","I1":"0.03"}]}';
                        alarm.setStatus(mapJsonToAlarmStatus(sampleStatus));
                        success = true;
                        loading.value = false;
                        return;
                    }

                    while (attempt < maxRetries && !success) {
                        statusReceived.value = false;

                        try {
                            console.log(`Attempt ${attempt + 1} of ${maxRetries}`);
                            const startedAtMs = Date.now();
                            await alarm.getStatus();

                            const received = await waitForStatus(startedAtMs, 15000);

                            if (received) {
                                success = true;
                                break;
                            } else {
                                console.warn("No status received within 15 seconds.");
                            }
                        } catch (e) {
                            console.warn(`Status call threw error:`, e);
                        }

                        attempt++;
                        if (attempt < maxRetries) {
                            console.warn("Retrying...");
                        }
                    }
                } catch (e) {
                    console.error("Message hub setup failed:", e);
                    app.error = "Unable to initialize system.";
                }

                loading.value = false;

                if (!success) {
                    app.error = "Gateway is not responding. Logging out...";
                    logoutTimerId = setTimeout(() => logout(), 4000);
                    clearErrorTimerId = setTimeout(() => { app.error = ""; }, 8000);
                }
            });

            const { showInstallMessage } = useIOSDetection();

            return {
                loading,
                container,
                error
            };
        }
    });
</script>